import { buildGridLines } from "./overlays/grid";
import { buildPositionLineInstances, drawPositionZones } from "./overlays/positions/positionGeometry";
import { MAIN_PANE_ID } from "./indicators/indicatorTypes";
import { drawIndicatorPanes } from "./indicators/drawIndicatorPanes";

// Grid and overlay lines are built directly in device pixels
const IDENTITY_TRANSFORM = [1, 0];

/**
 * Draws the WebGL layer: background, grid, position zones, the main series,
 * price indicators, the position lines and the drawings of the user above
 * them, clipped to the price pane (the dashed last price line is on the text layer), then the
 * oscillator panes under it.
 * The series moves with pan and zoom through the x / y transforms only.
 *
 * @param {object} params
 * @param {object} params.glLayer
 * @param {object} params.gridLines    - line renderer of the grid
 * @param {object} params.series       - mainSeries
 * @param {object} params.indicators   - createIndicators
 * @param {object} params.drawings     - createDrawings
 * @param {object} params.paneGridLines - line renderer of the oscillator pane grid
 * @param {object} params.overlayLines - line renderer of lines above the series
 * @param {object} params.positionZones - rect renderer of the position zones
 * @param {{ lines: object[], zones: object[] }} params.positionScene - from resolvePositionScene
 * @param {object} params.layout - from layoutFrame
 * @param {Array<{ x: number }>} params.timeTicks
 * @param {object} params.timeScale
 * @param {object} params.priceScale
 * @param {number} params.pixelRatio
 * @param {{ colors: object }} params.theme
 * @param {boolean} params.isSnappedX - false while the view follows time: whatever moves
 *   with the bars (series, histograms, vertical grid lines) stays sub-pixel
 */
export const drawGeometry = ({
  glLayer,
  gridLines,
  series,
  indicators,
  drawings,
  paneGridLines,
  overlayLines,
  positionZones,
  positionScene,
  layout,
  timeTicks,
  timeScale,
  priceScale,
  pixelRatio,
  theme,
  isSnappedX,
}) => {
  const { pane, priceLabels } = layout;
  const { colors } = theme;
  const viewport = glLayer.viewport;
  const deviceLines = { viewport, xTransform: IDENTITY_TRANSFORM, yTransform: IDENTITY_TRANSFORM };

  glLayer.beginFrame(colors.background.rgba);
  glLayer.clipTo({
    x: 0,
    y: 0,
    width: Math.round(pane.width * pixelRatio),
    height: Math.round(pane.height * pixelRatio),
  });

  gridLines.setInstances(
    buildGridLines({
      timeXs: timeTicks.map((tick) => tick.x),
      priceYs: priceLabels.map((label) => label.y),
      pane,
      pixelRatio,
      color: colors.grid.rgba,
      floatsPerLine: gridLines.floatsPerInstance,
      isSnappedX,
    }),
  );
  gridLines.draw(deviceLines);
  drawPositionZones({
    renderer: positionZones,
    zones: positionScene.zones,
    pane,
    pixelRatio,
    viewport,
    colors,
  });

  series.draw({ viewport, timeScale, priceScale, pixelRatio, paneHeight: pane.height, isSnappedX });
  indicators.draw(MAIN_PANE_ID, { viewport, timeScale, priceScale, top: 0, pixelRatio, isSnappedX });

  const floatsPerLine = overlayLines.floatsPerInstance;
  const positionLines = buildPositionLineInstances({
    lines: positionScene.lines,
    pane,
    pixelRatio,
    floatsPerLine,
    colors,
  });

  overlayLines.setInstances(positionLines);
  overlayLines.draw(deviceLines);
  drawings.draw({ viewport, pane, pixelRatio });

  drawIndicatorPanes({
    glLayer,
    gridLines: paneGridLines,
    indicators,
    layout,
    timeTicks,
    timeScale,
    pixelRatio,
    isSnappedX,
    colors: {
      background: colors.paneBackground.rgba,
      grid: colors.paneGrid.rgba,
      separator: colors.paneSeparator.rgba,
    },
  });
};
